import React from 'react';
import { motion } from 'framer-motion';
import './FlightRow.css';

const FlightRow = ({ flight, index, type = 'arrivals' }) => {
  // Get logo URL - prefer local path if available, otherwise use remote URL
  const getLogoUrl = () => {
    if (flight.logoPath) {
      // Use local logo from /logos directory
      const filename = flight.logoPath.split('/').pop() || flight.logoPath.split('\\').pop();
      return `/logos/${filename}`;
    }
    return flight.logoUrl;
  };

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase() || '';
    // Critical/Urgent statuses - bright, attention-grabbing colors
    if (statusUpper.includes('CANCELLED')) return '#ef4444'; // Red - urgent attention
    if (statusUpper.includes('FINAL CALL')) return '#ef4444'; // Red - urgent attention, blinking
    if (statusUpper.includes('DELAYED')) return '#f59e0b'; // Orange - warning
    if (statusUpper.includes('GATE CLOSED')) return '#f97316'; // Bright orange - urgent
    if (statusUpper.includes('CHECK-IN CLOSED')) return '#ec4899'; // Bright pink/magenta - attention
    if (statusUpper.includes('BOARDING')) return '#1e40af'; // Dark blue - important action
    if (statusUpper.includes('DEPARTED')) return '#9333ea'; // Purple - completed
    if (statusUpper.includes('LANDED')) return '#10b981'; // Green - success
    if (statusUpper.includes('GATE')) return '#8b5cf6'; // Purple - informational
    return '#94a3b8'; // Gray - default/scheduled
  };

  const getStatusIcon = (status) => {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('LANDED')) return '✓';
    if (statusUpper.includes('DELAYED')) return '⏱';
    if (statusUpper.includes('CANCELLED')) return '✕';
    if (statusUpper.includes('BOARDING')) return '';
    if (statusUpper.includes('GATE CLOSED')) return '';
    if (statusUpper.includes('CHECK-IN CLOSED')) return '';
    if (statusUpper.includes('DEPARTED')) return '✈️';
    if (statusUpper.includes('GATE')) return '📍';
    return '○';
  };

  const statusColor = getStatusColor(flight.status);
  const statusIcon = getStatusIcon(flight.status);
  const isFinalCall = flight.status?.toUpperCase().includes('FINAL CALL');
  const isBoarding = flight.status?.toUpperCase().includes('BOARDING');

  const isDelayed = flight.estimatedTime && 
                    flight.scheduledTime && 
                    flight.estimatedTime !== flight.scheduledTime;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={isFinalCall ? {
        opacity: 1,
        x: 0,
        backgroundColor: [
          'rgba(239, 68, 68, 0.05)',
          'rgba(239, 68, 68, 0.15)',
          'rgba(239, 68, 68, 0.05)'
        ]
      } : {
        opacity: 1,
        x: 0
      }}
      exit={{ opacity: 0, x: 20 }}
      transition={isFinalCall ? {
        opacity: { duration: 0.3, delay: index * 0.02 },
        x: { duration: 0.3, delay: index * 0.02 },
        backgroundColor: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
      } : { 
        duration: 0.3,
        delay: index * 0.02
      }}
      className={`flight-row ${isFinalCall ? 'final-call-row' : ''}`}
    >
      <div className="row-content">
        <div className="col-flight">
          <div className="flight-header">
            {getLogoUrl() && (
              <img 
                src={getLogoUrl()} 
                alt={flight.airlineCode}
                className="airline-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div className="flight-info">
              <div className="flight-number">{flight.flightNumber}</div>
              <div className="airline-code">{flight.airlineCode}</div>
            </div>
          </div>
        </div>
        
            <div className="col-origin">
              <div className="origin-text">
                {type === 'departures' 
                  ? (flight.airlineCode === 'Other' || !flight.airlineCode ? 'N/A' : (flight.destination || 'N/A'))
                  : (flight.origin || 'N/A')
                }
              </div>
            </div>
        
        <div className="col-time">
          <div className="time-text">{flight.scheduledTime || '--:--'}</div>
        </div>
        
        <div className="col-estimated">
          <motion.div 
            className={`estimated-text ${isDelayed ? 'delayed' : ''}`}
            animate={isDelayed ? { 
              color: ['#f59e0b', '#fbbf24', '#f59e0b']
            } : {}}
            transition={{ 
              duration: 2,
              repeat: isDelayed ? Infinity : 0
            }}
          >
            {flight.estimatedTime || flight.scheduledTime || '--:--'}
          </motion.div>
        </div>
        
        <div className="col-terminal">
          <div className="terminal-badge">{flight.terminal || '--'}</div>
        </div>
        
        <div className="col-status">
          <div className="status-badge-wrapper">
            <motion.div 
              className="status-badge"
              style={{ 
                backgroundColor: statusColor,
                borderColor: statusColor,
                color: '#ffffff',
                fontWeight: '700',
                boxShadow: `0 2px 8px ${statusColor}40`
              }}
              whileHover={{ scale: 1.05 }}
              animate={isFinalCall ? {
                opacity: [1, 0.5, 1],
                boxShadow: [
                  `0 2px 8px ${statusColor}40`,
                  `0 4px 16px ${statusColor}80`,
                  `0 2px 8px ${statusColor}40`
                ]
              } : isBoarding ? {
                scale: [1, 1.05, 1],
                boxShadow: [
                  `0 2px 8px ${statusColor}40`,
                  `0 4px 20px ${statusColor}90`,
                  `0 2px 8px ${statusColor}40`
                ]
              } : {
                boxShadow: [
                  `0 2px 8px ${statusColor}40`,
                  `0 2px 12px ${statusColor}60`,
                  `0 2px 8px ${statusColor}40`
                ]
              }}
              transition={{
                duration: isFinalCall ? 0.8 : isBoarding ? 1.2 : 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <span className="status-icon">{statusIcon}</span>
              <span className="status-text">{flight.status || 'SCHEDULED'}</span>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(FlightRow);

